/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
/* eslint-disable no-param-reassign */

import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Query } from '@apollo/client/react/components';
import { gql } from '@apollo/client';
import mbpLogger from 'mbp-logger';
import { object } from 'prop-types';
import { compose } from 'recompose';
import { makeStyles } from '@material-ui/core';
import * as app from '../../../../../../state/ducks/App';
import { GRAPHQL_ENV } from '../../../../../gql';
import { trackPage } from '../../../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../../../helpers/tracking/hocs/withPageView';
import Lander from './Lander';
import Reference from './includeRefs';

import GraphqlBreadcrumbs from '../../../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import GraphqlSeoHeadTags from '../../../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import Graphql404ErrorContainer from '../../../Graphql404Error/Graphql404ErrorContainer';
import isSingleSource from '../../../../../helpers/isSingleSource';

const brandSelectors = app.ducks.brand.selectors;

const useStyles = makeStyles((theme) => ({
    desktop: {
        display: 'block',
        maxWidth: '1440px',
        width: '100%',
        margin: '0 auto',
        padding: '10px 0px',
        [theme.breakpoints.down(1460)]: {
            padding: '0px 0px 8px 10px',
        },
        [theme.breakpoints.down(600.5)]: {
            display: 'none',
        },
    },
    mobile: {
        padding: '20px 0 0 20px',
        [theme.breakpoints.up(600.5)]: {
            display: 'none',
        },
    },
}));

const LanderContainer = ({
    brand, page,
}) => {
    const classes = useStyles();

    const useSingleSource = isSingleSource(page?.type, page?.path);
    let altPath = '';
    if (useSingleSource.brand) {
        brand = useSingleSource.brand;
        altPath = useSingleSource.path;
        page.content_type = useSingleSource.content_type;
        // isCelebrationsLander = useSingleSource.celebrations || false;
    }

    const UNIVERSALLANDERCONTAINER_QUERY = gql`
            query UniversalLanderContainer {
                 findContent(
                    brand: "${brand['domain-name']}",
                    environment: "${GRAPHQL_ENV}",
                    locale: "en-us",
                    query:"{\\"url\\": {\\"$in\\": [\\"${altPath || page?.path}\\"]}}",
                    contentType: "universal_lander",
                    includeRefs: ${Reference},
                    ) {
                        content
                    }
            }
        `;
    return (
        <Query query={UNIVERSALLANDERCONTAINER_QUERY}>
            {({ loading, error, data }) => {
                if (loading) {
                    return <div />; // skeleton component
                }

                if (error) {
                    mbpLogger.logError({
                        appName: process.env.npm_package_name,
                        query: UNIVERSALLANDERCONTAINER_QUERY,
                        component: 'LanderContainer.js',
                        message: 'Error loading data from Graphql',
                        env: GRAPHQL_ENV,
                        error,
                    });

                    return null;
                }

                if (!data) {
                    mbpLogger.logWarning({
                        appName: process.env.npm_package_name,
                        query: UNIVERSALLANDERCONTAINER_QUERY,
                        component: 'LanderContainer.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });

                    return <Graphql404ErrorContainer brand={brand} page={page} />;
                }
                if (!data.findContent?.content) {
                    mbpLogger.logWarning({
                        appName: process.env.npm_package_name,
                        query: UNIVERSALLANDERCONTAINER_QUERY,
                        data,
                        component: 'LanderContainer.js',
                        message: 'No content returned for query',
                    });
                    return <Graphql404ErrorContainer brand={brand} page={page} />;
                }

                const rows = data.findContent.content?.entries?.[0]?.rows || null;
                if (!rows) {
                    mbpLogger.logWarning({
                        appName: process.env.npm_package_name,
                        query: UNIVERSALLANDERCONTAINER_QUERY,
                        data,
                        component: 'LanderContainer.js',
                        message: 'No row content has been added to this page',
                    });
                    return <Graphql404ErrorContainer brand={brand} page={page} />;
                }
                const trackData =  data.findContent.content.entries?.[0]?.tracking || {};

                // const untData = data.findContentPage.underNavText?.entries || null;
                const SEO = data.findContent.content.entries?.[0]?.seo || null;
                // const domainURL = `https://pwa.${brand.domain}.com` || '';

                // showing breadcrumb universal-lander
                const hasBreadcrumb = SEO?.breadcrumb?.length > 0;
                const universalBreadCrumb =  (classRef) => (
                    hasBreadcrumb && <div className={classes[classRef]}><GraphqlBreadcrumbs breadCrumbArray={SEO.breadcrumb} /></div>
                );

                // eslint-disable-next-line no-param-reassign
                page.page_settings = data.findContent.content.entries?.[0]?.page_settings || { page_margin: false };
                return (
                    <div data-pagetype="universal-lander">
                        {universalBreadCrumb('desktop')}
                        <GraphqlSeoHeadTags seoData={SEO} />
                        <Lander
                            rows={rows}
                            page={page}
                            trackData={trackData}
                        />
                        {universalBreadCrumb('mobile')}
                    </div>
                );
            }}
        </Query>
    );
};

LanderContainer.propTypes = {
    brand: object.isRequired,
    page: object.isRequired,
};

const mapStateToProps = (state) => ({
    brand: brandSelectors.getBrand(state),
});

const enhance = compose(
    withRouter,
    connect(mapStateToProps),
    trackPage({
        type: 'universal-lander',
        name: 'Universal lander',
        pageType: 'universal-lander',
        pageName: 'Universal lander',
    }),
    withPageView({
        pageViewSuccessTest: () => true,
        updateEveryRender: true,
    }),
);

export default enhance(LanderContainer);
